import React from 'react';
import CategoryPageLinkGrid, {
    LINK_GRID_MAX_ITEMS,
} from '@ui-rocco/pages/category-page/CategoryPageLinkGrid';

/** A professional category that has a "{category} in {place}" page. */
export interface CategoryInLocation {
    Link: string;
    Title: string;
    ItemsCount: number;
}

interface CategoriesInLocationProps {
    /** Display name of the place (e.g. "Austin"), used in heading and anchors. */
    locationName: string;
    categories: CategoryInLocation[];
    stackedAfterMeshSection?: boolean;
}

/**
 * Location-side node of the link mesh and the geographic counterpart to
 * {@link CategoryPageLocations}: on a location hub it lists the categories
 * available in the place, and on a category × location page it surfaces sibling
 * categories in the same place, making the mesh bidirectional. Renders nothing
 * when there are no categories or no place name.
 *
 * Incoming categories arrive ordered by professional count (most-populated
 * first). We keep the highest-value subset the grid will render, then display it
 * alphabetically so the links are easy to scan without sacrificing the
 * internal-link curation.
 */
const CategoryPageCategoriesInLocation = ({
    locationName,
    categories,
    stackedAfterMeshSection = false,
}: CategoriesInLocationProps) => {
    if (!locationName) {
        return null;
    }

    const items = categories
        .slice(0, LINK_GRID_MAX_ITEMS)
        .sort((a, b) => a.Title.localeCompare(b.Title))
        .map((category) => ({
            key: category.Link,
            href: category.Link,
            label: `${category.Title} in ${locationName}`,
        }));

    return (
        <CategoryPageLinkGrid
            heading={`More Building & Design Professionals in ${locationName}`}
            items={items}
            stackedAfterMeshSection={stackedAfterMeshSection}
            flowDownColumns
        />
    );
};

export default CategoryPageCategoriesInLocation;
